/* ──────────────────────────────────────────────────────────
   Company-Defaults side-form
   • “Payments” accordion removed
   • New accordion “Department” keeps the same 5 buttons
   ────────────────────────────────────────────────────────── */
   import React, { useState } from "react";
   import PropTypes from "prop-types";
   import { CircularProgress, Box } from "@mui/material";
   
   import ModalHeader    from "../components/ViewModalHeader";
   import ModalAccordion from "../../../../../components/Modals/ModalAccordion";
   import RenderFields   from "../../../../../components/Modals/ModalFields/RenderFields";
   import AlertSnackbar  from "../../../../../components/Notifications/AlertSnackbar";
   
   import HolidayCalendar   from "./Department/HolidayCalendar";
   import DepartmentSetup   from "./Department/DepartmentSetup";
   import CostingRatesSetup from "./Department/CostingRatesSetup";
   import EmployeeCostSetup from "./Department/EmployeeCost";
   import PayrollCodeSetup  from "./Department/PayrollCodeSetup";
   
   import {
     CompanyDefaultsGeneralInfo,
     CompanyDefaultsAddressInfo,
     CompanyDefaultsCommunicationInfo,
   } from "../hooks/companyDefaultsDataConfig";
   
   /* buttons that open the RHS panels */
   const INLINE_FEATURES = [
     "Departments","HolidayCalendar","CostingRates",
     "EmployeeCost","PayrollCodes",
     "PostingAccounts","InvoicingSetup","UserSecurity","EmailNotifications",
   ];
   
   /* tiny shared button classes */
   const smallBtn =
     "border rounded px-3 py-1 text-xs bg-gray-100 hover:bg-cyan-100 disabled:opacity-40";
   
   export default function CompanyDefaultsSideFormInline({
     feature, setFeature,
     tableById = {},
     isEditing, setIsEditing,
     data, id,
     onPrepareNew,
     onFieldChange,
     isLoadingDetails,
   }) {
     /* UI state */
     const [tabs,       setTabs      ] = useState([]);
     const [isInfoOpen, setIsInfoOpen] = useState(false);
     const [isPanelOff, setIsPanelOff] = useState(false);
     const [lastOpen,   setLastOpen  ] = useState(null);
     const [snack,      setSnack     ] = useState({open:false,message:"",severity:"info"});
     const [isExpanded, setIsExpanded] = useState(false);
   
     /* title-case helper */
     const titleCase = s => s.replace(/([A-Z])/g," $1").replace(/^./,c=>c.toUpperCase());
   
     /* open / close tab logic */
     const open = key => {
       setFeature(key);
       if (!tabs.includes(key)) setTabs(t => [...t, key]);
       setIsPanelOff(false);
       setIsInfoOpen(false);
     };
     const close = key => {
       setTabs(t => t.filter(k => k!==key));
       if (feature===key) {
         const remaining = tabs.filter(k=>k!==key);
         const next = remaining.at(-1) ?? null;
         setFeature(next);
         if (!next) setIsInfoOpen(false);
         if (!next && !isInfoOpen) setIsPanelOff(true);
       }
     };
   
     /* fold / unfold RHS panel */
     const togglePanel = () => {
       if (isEditing) return;
       if (isPanelOff) {                       // bring it back
         setIsPanelOff(false);
         if (lastOpen==="INFO") setIsInfoOpen(true);
         else if (lastOpen)     setFeature(lastOpen);
         else                   setIsInfoOpen(true);
       } else {                                // hide it
         setIsPanelOff(true);
         setLastOpen(feature ?? (isInfoOpen?"INFO":null));
         setFeature(null);
         setIsInfoOpen(false);
       }
     };
   
     /* map key → component */
     const panelFor = k => {
       switch (k) {
         case "Departments":     return <DepartmentSetup   onClose={()=>close(k)} />;
         case "HolidayCalendar": return <HolidayCalendar   onClose={()=>close(k)} />;
         case "CostingRates":    return <CostingRatesSetup onClose={()=>close(k)} />;
         case "EmployeeCost":    return <EmployeeCostSetup onClose={()=>close(k)} />;
         case "PayrollCodes":    return <PayrollCodeSetup  onClose={()=>close(k)} />;
         default:                return tableById[k] ?? <div className="p-4 text-gray-500">No component for “{k}”.</div>;
       }
     };
   
     const rhsOpen = !isPanelOff && (feature || isInfoOpen);
   
     /* ───────────────────────── render ───────────────────────── */
     return (
       <div className="flex flex-col h-full w-full">
   
         {/* header (unchanged) */}
         <ModalHeader
           title={`Company Defaults ${id ? `(#${id})` : ""}`}
           id={id}
           isEditMode={isEditing}
           handleEditButtonClick={()=>setIsEditing(v=>!v)}
           handleSave={()=>{/* connect */}}
           handleDelete={()=>{/* connect */}}
           handleOpenAddModal={onPrepareNew}
           isPanelOff={isPanelOff}
           onTogglePanel={togglePanel}
           isExpanded={isExpanded}
           setIsExpanded={setIsExpanded}
         />
   
         {/* main split row */}
         <div className="flex flex-1 overflow-hidden">
   
           {/* LEFT – form */}
           <div className={`overflow-y-auto p-4 transition-all duration-300 ${rhsOpen ? "w-[35%]" : "w-full"}`}>
   
             {isLoadingDetails ? (
               <Box sx={{display:"flex",justifyContent:"center",alignItems:"center",p:3,height:"300px"}}>
                 <CircularProgress/>
               </Box>
             ) : (
               [
                 { label:"General",       fields:CompanyDefaultsGeneralInfo },
                 { label:"Address",       fields:CompanyDefaultsAddressInfo },
                 { label:"Communication", fields:CompanyDefaultsCommunicationInfo },
   
                 /* NEW accordion – no fields – keeps Dept-buttons */
                 { label:"Department",    fields:[] },
               ].map(({label,fields},idx) => (
                 <ModalAccordion key={label} label={label} defaultExpanded={idx===0}>
                   {/* form body (only if there are fields) */}
                   {fields.length>0 && (
                     <div className="grid md:grid-cols-2 gap-4">
                       <RenderFields
                         fields={fields}
                         formData={data}
                         handleChange={onFieldChange}
                         readOnly={!isEditing}
                         validationErrors={{}}
                       />
                     </div>
                   )}
   
                   {/* button clusters */}
                   {label==="General" && (
                     <div className="mt-4 flex flex-wrap gap-2">
                       {[
                         ["Posting Accounts","PostingAccounts"],
                         ["Invoice Setup","InvoicingSetup"],
                         ["User Security","UserSecurity"],
                         ["Email Notifications","EmailNotifications"],
                       ].map(([txt,k])=>(
                         <button key={k} disabled={isEditing} onClick={()=>open(k)} className={smallBtn}>{txt}</button>
                       ))}
                     </div>
                   )}
   
                   {label==="Department" && (
                     <div className="mt-4 flex flex-wrap gap-2">
                       {[
                         ["Departments","Departments"],
                         ["Holiday Calendar","HolidayCalendar"],
                         ["Costing Setup","CostingRates"],
                         ["Employee Cost","EmployeeCost"],
                         ["PR Code Setup","PayrollCodes"],
                       ].map(([txt,k])=>(
                         <button key={k} disabled={isEditing} onClick={()=>open(k)} className={smallBtn}>{txt}</button>
                       ))}
                     </div>
                   )}
                 </ModalAccordion>
               ))
             )}
           </div>
   
           {/* RIGHT – tabs / content */}
           {rhsOpen && (
             <div className="border-l flex flex-col w-[65%] bg-gray-50 overflow-hidden">
   
               {/* tab bar */}
               {tabs.length>0 && (
                 <div className="flex space-x-1 border-b bg-white px-2 py-1 overflow-x-auto">
                   {tabs.map(k=>(
                     <div key={k}
                       className={`flex items-center space-x-1 px-3 py-1 rounded-t ${
                         feature===k ? "bg-gray-100 font-semibold"
                                      : "bg-gray-200 hover:bg-gray-300 cursor-pointer"}`}
                       onClick={()=>setFeature(k)}>
                       <span className="text-xs">{titleCase(k)}</span>
                       <button onClick={e=>{e.stopPropagation(); close(k);}}
                               className="leading-none text-sm hover:text-red-600">×</button>
                     </div>
                   ))}
                 </div>
               )}
   
               {/* active panel */}
               <div className="flex-1 overflow-y-auto">
                 {feature ? panelFor(feature)
                          : (isInfoOpen && <div className="p-4">Info panel content…</div>)}
               </div>
             </div>
           )}
         </div>
   
         {/* snackbar */}
         <AlertSnackbar {...snack} onClose={()=>setSnack({...snack,open:false})}/>
       </div>
     );
   }
   
   /* ───────────────────────── PropTypes ───────────────────────── */
   CompanyDefaultsSideFormInline.propTypes = {
     feature:          PropTypes.string,
     setFeature:       PropTypes.func.isRequired,
     tableById:        PropTypes.object,
     isEditing:        PropTypes.bool,
     setIsEditing:     PropTypes.func.isRequired,
     data:             PropTypes.object.isRequired,
     id:               PropTypes.oneOfType([PropTypes.string,PropTypes.number,PropTypes.oneOf([null])]),
     onPrepareNew:     PropTypes.func.isRequired,
     onFieldChange:    PropTypes.func.isRequired,
     isLoadingDetails: PropTypes.bool,
   };
   